<template>
  <v-container fluid class="promotions-page">
    <!-- TOP HEADER -->
    <div class="ps-page-header d-flex align-center justify-space-between flex-wrap mb-4" style="gap: 16px;">
      <div class="d-flex align-center" style="gap: 12px;">
        <span class="ap-card-accent"></span>
        <div>
          <div class="d-flex align-center" style="gap: 8px;">
            <h1 class="ps-page-title mb-0">{{ $t("manage_promotions") }}</h1>
            <v-chip x-small color="primary" outlined class="font-weight-bold">
              {{ pagination.total }} {{ $t("items") || "items" }}
            </v-chip>
          </div>
          <div class="ps-page-subtitle">{{ $t("promotion_page_subtitle") }}</div>
        </div>
      </div>

      <div class="d-flex align-center" style="gap: 10px;">
        <v-btn
          depressed
          color="primary"
          class="white--text font-weight-bold text-none rounded-lg px-4"
          @click="goCreate"
        >
          <v-icon left small>fal fa-plus-circle</v-icon>
          {{ $t("create_promotion") }}
        </v-btn>
      </div>
    </div>

    <!-- METRICS KPI STRIP -->
    <KpiCards class="mb-4" :items="kpiItems" :loading="loading" :skeleton="4" />

    <!-- FILTER TOOLBAR CARD -->
    <v-card class="ap-card mb-4" outlined elevation="0">
      <div class="pa-3 pa-md-4">
        <!-- Top Filter Row: Search + Status Segmented Tabs -->
        <div class="d-flex align-center justify-space-between flex-wrap mb-3" style="gap: 12px;">
          <div class="d-flex align-center flex-grow-1" style="max-width: 440px; min-width: 260px;">
            <TextSearch
              v-model="search"
              :placeholder="$t('search_by_promotion_code')"
              @search="fetchPromotions(1)"
              @onClear="
                search = '';
                fetchPromotions(1);
              "
            />
          </div>

          <!-- Status Segmented Tabs (.br-tabs style) -->
          <div class="br-tabs">
            <div
              v-for="filter in statusFilters"
              :key="filter.value"
              class="br-tab"
              :class="{ 'br-tab--active': activeStatusFilter === filter.value }"
              @click="activeStatusFilter = filter.value"
            >
              {{ filter.label }}
              <span class="br-tab-badge ml-1">{{ statusFilterCount(filter.value) }}</span>
            </div>
          </div>
        </div>

        <!-- Bottom Filter Row: Type Filters Pills -->
        <div class="d-flex align-center flex-wrap pt-2 border-top" style="gap: 8px;">
          <span class="text-caption font-weight-bold grey--text text-uppercase mr-2">
            <v-icon x-small color="grey" class="mr-1">fal fa-filter</v-icon>
            {{ $t("promotion_type") }}:
          </span>
          <button
            v-for="filter in typeFilters"
            :key="filter.value"
            type="button"
            class="ps-filter-pill"
            :class="{ 'ps-filter-pill--active': activeFilter === filter.value }"
            @click="activeFilter = filter.value"
          >
            {{ filter.label }}
            <span class="ps-filter-count">{{ typeFilterCount(filter.value) }}</span>
          </button>
        </div>
      </div>
    </v-card>

    <!-- PROMOTIONS GRID -->
    <!-- Loading Skeletons -->
    <v-row v-if="loading">
      <v-col v-for="n in 4" :key="`skeleton-${n}`" cols="12" md="6">
        <v-card outlined class="ap-card pa-4">
          <div class="d-flex" style="gap: 14px;">
            <v-skeleton-loader type="image" width="84" height="84" class="rounded-lg shrink-0" />
            <div class="flex-grow-1">
              <v-skeleton-loader type="heading" class="mb-2" />
              <v-skeleton-loader type="text" width="60%" class="mb-2" />
              <v-skeleton-loader type="chip" width="120" />
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Empty State -->
    <v-card v-else-if="!displayedPromotions.length" class="ap-card pa-12 text-center" outlined elevation="0">
      <div class="ps-empty">
        <v-icon size="56" class="ps-empty-icon mb-3">fal fa-tags</v-icon>
        <div class="font-weight-bold text-subtitle-1 mb-1">{{ $t("no_promotions_found") }}</div>
        <div class="text-caption grey--text mb-4">{{ $t("try_another_search_or_create_promotion") }}</div>
        <v-btn
          depressed
          color="primary"
          class="white--text font-weight-bold text-none rounded-lg px-5"
          @click="goCreate"
        >
          <v-icon left small>fal fa-plus-circle</v-icon>
          {{ $t("create_promotion") }}
        </v-btn>
      </div>
    </v-card>

    <!-- Promo Cards Grid -->
    <v-row v-else>
      <v-col
        v-for="item in displayedPromotions"
        :key="item.id"
        cols="12"
        md="6"
      >
        <v-card
          outlined
          elevation="0"
          class="ap-card ps-promo-card d-flex flex-column"
          :class="{ 'ps-promo-card--inactive': !item.is_active }"
        >
          <div class="pa-4 flex-grow-1 d-flex flex-column" style="gap: 12px;">
            <!-- Top Row: Type Badge + Status Pill -->
            <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px;">
              <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                <span class="ps-promo-type-badge" :class="`ps-promo-type--${item.promotion_type}`">
                  <v-icon x-small class="mr-1" :color="promoTypeColor(item.promotion_type)">
                    {{ promoTypeIcon(item.promotion_type) }}
                  </v-icon>
                  {{ $t(item.promotion_type) }}
                </span>
                <span class="ps-target-badge">
                  {{ targetLabel(item.target_type) }}
                </span>
              </div>

              <span
                class="stock-pill"
                :class="item.is_active ? 'stock-pill-normal' : 'stock-pill-slate'"
              >
                {{ item.is_active ? $t("active") : $t("inactive") }}
              </span>
            </div>

            <!-- Main Info Row: Thumbnail + Title & Benefit -->
            <div class="d-flex" style="gap: 14px;">
              <div class="ps-promo-thumb-wrap shrink-0">
                <v-img
                  v-if="item.image"
                  :src="promotionImage(item.image)"
                  width="74"
                  height="74"
                  class="rounded-xl ps-promo-photo"
                  cover
                />
                <div v-else class="ps-promo-photo-placeholder rounded-xl">
                  <v-icon size="28" color="#5b39c9">fal fa-gift</v-icon>
                </div>
              </div>

              <div class="min-w-0 flex-grow-1">
                <div class="d-flex align-center flex-wrap mb-1" style="gap: 8px;">
                  <h3 class="ps-promo-name text-truncate mb-0" :title="item.name">
                    {{ item.name }}
                  </h3>
                  <!-- Coupon Code Ticket -->
                  <span class="ps-code-ticket" :title="$t('promotion_code')">
                    <v-icon x-small color="#5b39c9" class="mr-1">fal fa-ticket</v-icon>
                    {{ item.code }}
                  </span>
                </div>

                <div class="ps-promo-desc text-truncate mb-2" :title="promotionDescription(item)">
                  {{ promotionDescription(item) }}
                </div>

                <!-- Schedule & Days -->
                <div class="d-flex align-center flex-wrap text-caption grey--text text--darken-1" style="gap: 10px;">
                  <span class="d-flex align-center">
                    <v-icon x-small color="#64748b" class="mr-1">fal fa-calendar-alt</v-icon>
                    {{ dateRange(item) }}
                  </span>
                  <span v-if="activeDaysLabel(item)" class="d-flex align-center">
                    <v-icon x-small color="#64748b" class="mr-1">fal fa-repeat</v-icon>
                    {{ activeDaysLabel(item) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Usage Progress Tracker -->
            <div class="ps-usage-tracker pa-2 rounded-lg">
              <div class="d-flex justify-space-between align-center text-caption mb-1">
                <span class="grey--text font-weight-medium">
                  <v-icon x-small color="grey" class="mr-1">fal fa-user-check</v-icon>
                  {{ $t("total_uses") }}:
                </span>
                <span class="font-weight-bold" style="color: #141021;">
                  {{ usageLabel(item) }}
                </span>
              </div>
              <v-progress-linear
                :value="usagePercent(item)"
                height="5"
                rounded
                color="primary"
                background-color="#e2e8f0"
              />
            </div>
          </div>

          <!-- Bottom Actions Bar -->
          <div class="py-2 px-4 border-top d-flex align-center justify-space-between bg-slate-50">
            <div class="d-flex align-center" style="gap: 8px;" @click.stop>
              <v-switch
                inset
                dense
                hide-details
                color="primary"
                class="mt-0 pt-0"
                :disabled="statusLoadingId === item.id"
                :input-value="!!item.is_active"
                @change="togglePromotionStatus(item, $event)"
              />
              <span class="text-caption font-weight-bold" :class="item.is_active ? 'primary--text' : 'grey--text'">
                {{ item.is_active ? $t("active") : $t("inactive") }}
              </span>
            </div>

            <div class="d-flex align-center" style="gap: 6px;">
              <button
                type="button"
                class="ps-act"
                :disabled="isHqLocked(item)"
                :title="isHqLocked(item) ? $t('locked_by_hq') : $t('edit')"
                @click="goEdit(item.id)"
              >
                <v-icon x-small>fal fa-pen</v-icon>
              </button>
              <button
                type="button"
                class="ps-act ps-act--danger"
                :disabled="isHqLocked(item)"
                :title="isHqLocked(item) ? $t('locked_by_hq') : $t('delete')"
                @click="deletePromotion(item)"
              >
                <v-icon x-small>fal fa-trash-alt</v-icon>
              </button>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- PAGINATION FOOTER -->
    <div
      v-if="pagination.total > 0"
      class="mt-4 ap-card pa-3 d-flex align-center justify-space-between flex-wrap"
      style="gap: 12px;"
    >
      <div class="d-flex align-center text-caption grey--text" style="gap: 12px;">
        <span>{{ $t("rows_per_page") }}:</span>
        <v-select
          v-model="perPage"
          :items="[10, 20, 30, 50]"
          dense
          outlined
          hide-details
          class="ps-per-page-select"
        />
        <span>{{ pageRangeText }}</span>
      </div>

      <div class="d-flex align-center" style="gap: 6px;">
        <v-btn
          icon
          small
          outlined
          class="rounded-lg"
          :disabled="page <= 1"
          @click="fetchPromotions(page - 1)"
        >
          <v-icon small>fal fa-chevron-left</v-icon>
        </v-btn>
        <span class="font-weight-bold text-caption px-2">{{ page }} / {{ pagination.last_page }}</span>
        <v-btn
          icon
          small
          outlined
          class="rounded-lg"
          :disabled="page >= pagination.last_page"
          @click="fetchPromotions(page + 1)"
        >
          <v-icon small>fal fa-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>

    <app-loading :overlay="overlay" />
  </v-container>
</template>

<script>
import { confirm, success, toast } from "@/controllers/alert";
import { myMixin } from "@/mixin/main";
import Help from "@/utils/helper";
import {
  ACTIVE_DAY_OPTIONS,
  formatPromotionDateRange,
  formatPromotionTimeRange,
  PROMOTION_FILTERS,
  PROMOTION_STATUS_FILTERS,
} from "@/utils/promotions";

export default {
  name: "ManagePromotions",
  mixins: [myMixin],
  data() {
    return {
      overlay: false,
      loading: false,
      search: "",
      activeFilter: "all",
      activeStatusFilter: "all",
      page: 1,
      perPage: 30,
      pagination: {
        current_page: 1,
        last_page: 1,
        total: 0,
      },
      promotions: [],
      statusLoadingId: null,
      summary: {
        total: 0,
        active: 0,
        inactive: 0,
        total_uses: 0,
        total_discount: 0,
        by_type: {},
      },
    };
  },
  computed: {
    kpiItems() {
      return [
        {
          key: "total",
          label: this.$t("total_promotions") || "Total Promotions",
          icon: "fal fa-tags",
          iconColor: "primary",
          tone: "primary",
          value: this.pagination.total,
        },
        {
          key: "active",
          label: this.$t("active_promotions") || "Active Promotions",
          icon: "fal fa-bolt",
          iconColor: "success",
          tone: "success",
          value: this.statActiveCount,
        },
        {
          key: "uses",
          label: this.$t("total_uses") || "Total Uses",
          icon: "fal fa-chart-line",
          iconColor: "#0284c7",
          tone: "cyan",
          value: this.statTotalUses,
        },
        {
          key: "discount",
          label: this.$t("total_discount") || "Total Discount",
          icon: "fal fa-hand-holding-usd",
          iconColor: "#d97706",
          tone: "amber",
          value: this.statTotalDiscount,
          unit: "₭",
        },
      ];
    },
    typeFilters() {
      return PROMOTION_FILTERS.map((filter) => ({
        ...filter,
        label: this.$t(filter.labelKey),
      }));
    },
    statusFilters() {
      return PROMOTION_STATUS_FILTERS.map((filter) => ({
        ...filter,
        label: this.$t(filter.labelKey),
      }));
    },
    statActiveCount() {
      return this.summary.active || 0;
    },
    statTotalUses() {
      return this.summary.total_uses || 0;
    },
    statTotalDiscount() {
      return this.summary.total_discount || 0;
    },
    displayedPromotions() {
      return this.promotions;
    },
    pageRangeText() {
      if (!this.pagination.total) return "0 of 0";
      const start = (this.page - 1) * this.perPage + 1;
      const end = Math.min(
        start + this.promotions.length - 1,
        this.pagination.total
      );
      return `${start}-${end} of ${this.pagination.total}`;
    },
  },
  watch: {
    activeFilter() {
      this.fetchPromotions(1);
    },
    activeStatusFilter() {
      this.fetchPromotions(1);
    },
    perPage() {
      this.fetchPromotions(1);
    },
  },
  created() {
    this.fetchPromotions();
  },
  methods: {
    isHqLocked(item) {
      return Boolean(item?.is_hq);
    },
    promoTypeIcon(type) {
      switch (type) {
        case "buyx_gety":
          return "fal fa-gift";
        case "discount_amount":
          return "fal fa-money-bill-wave";
        case "bundle":
          return "fal fa-boxes";
        default:
          return "fal fa-percentage";
      }
    },
    promoTypeColor(type) {
      switch (type) {
        case "buyx_gety":
          return "#d97706";
        case "discount_amount":
          return "#0284c7";
        case "bundle":
          return "#0d9488";
        default:
          return "#5b39c9";
      }
    },
    usagePercent(item) {
      const limit = Number(item.usage_limit);
      const used = Number(item.used_count || 0);
      if (!limit || limit <= 0) return 0;
      return Math.min(100, Math.round((used / limit) * 100));
    },
    typeFilterCount(value) {
      if (value === "all") return this.summary.total || 0;
      return Number(this.summary.by_type?.[value] || 0);
    },
    statusFilterCount(value) {
      if (value === "all") return this.summary.total || 0;
      if (value === "active") return this.summary.active || 0;
      return this.summary.inactive || 0;
    },
    async fetchPromotions(page = this.page) {
      this.page = page;
      this.loading = true;
      try {
        const query = new URLSearchParams({
          page: this.page,
          per_page: this.perPage,
        });
        if (this.search) {
          query.append("q", this.search);
        }
        if (this.activeFilter !== "all") {
          query.append("promotion_type", this.activeFilter);
        }
        if (this.activeStatusFilter !== "all") {
          query.append("status", this.activeStatusFilter);
        }

        const { data } = await this.$http.get(`promotions_paginate?${query}`);
        if (data?.status) {
          const paginate = data.data?.paginate;
          this.promotions = Array.isArray(paginate?.data) ? paginate.data : [];
          this.pagination = {
            current_page: paginate?.current_page || 1,
            last_page: paginate?.last_page || 1,
            total: paginate?.total || this.promotions.length,
          };
          this.summary = {
            total: 0,
            active: 0,
            inactive: 0,
            total_uses: 0,
            total_discount: 0,
            by_type: {},
            ...data.data?.summary,
          };
        }
      } catch (error) {
        console.error("Failed to load promotions", error);
        this.promotions = [];
      } finally {
        this.loading = false;
      }
    },
    promotionImage(path) {
      return Help.img(path);
    },
    benefitSummary(item) {
      if (!item) return "";
      if (item.promotion_type === "buyx_gety") {
        return this.buyxGetySummary(item) || this.$t("buyx_gety");
      }
      const value = Number(item.discount_value || 0);
      const label = this.$t(item.promotion_type) || this.$t("discount");
      return item.discount_type === "amount"
        ? `${label} ${value}`
        : `${label} ${value}%`;
    },
    buyxGetySummary(item) {
      const participants = Array.isArray(item.participants) ? item.participants : [];
      const describe = (role) =>
        participants
          .filter((participant) => participant.role === role)
          .map((participant) => {
            const name = participant.product?.name || participant.category?.la_name || "";
            const qty = participant.qty || 1;
            return name ? `${name} x${qty}` : "";
          })
          .filter(Boolean)
          .join(", ");

      const buy = describe("buy");
      const get = describe("get");
      if (!buy || !get) return "";

      return this.$t("promotion_buy_get_hint", { buy, get });
    },
    promotionDescription(item) {
      const note = item?.note ? String(item.note).trim() : "";
      if (note) return note;
      return this.benefitSummary(item);
    },
    dateRange(item) {
      const dates = formatPromotionDateRange(item.start_date, item.end_date);
      const time = formatPromotionTimeRange(item.start_time, item.end_time);

      if (!dates && !time) return this.$t("promotion_no_schedule");
      if (!dates) return `${this.$t("time")} ${time}`;
      return time ? `${dates}, ${this.$t("time")} ${time}` : dates;
    },
    activeDaysLabel(item) {
      const days = Array.isArray(item.active_days) ? item.active_days : [];
      if (!days.length || days.length === ACTIVE_DAY_OPTIONS.length) return "";
      return ACTIVE_DAY_OPTIONS.filter((day) => days.includes(day))
        .map((day) => this.$t(`day_${day}`))
        .join(", ");
    },
    targetLabel(targetType) {
      switch (targetType) {
        case "category":
          return this.$t("category");
        case "product":
          return this.$t("product");
        default:
          return this.$t("all");
      }
    },
    usageLabel(item) {
      const used = Number(item.used_count || 0);
      if (item.usage_limit === null || item.usage_limit === undefined) {
        return `${used} / ${this.$t("usage_limit_placeholder") || "∞"}`;
      }
      return `${used} / ${item.usage_limit}`;
    },
    goCreate() {
      this.$router.push("/manage-promotions/new");
    },
    goEdit(id) {
      this.$router.push(`/manage-promotions/${id}`);
    },
    async togglePromotionStatus(item, value) {
      const previous = Number(item.is_active);
      item.is_active = value ? 1 : 0;
      this.statusLoadingId = item.id;

      try {
        const { data } = await this.$http.put(`promotions/${item.id}`, {
          is_active: item.is_active,
        });
        if (data?.status) {
          await toast(this, data.message || "save_promotion_success", "success");
          return;
        }
        item.is_active = previous;
      } catch (error) {
        console.error("Failed to toggle promotion status", error);
        item.is_active = previous;
      } finally {
        this.statusLoadingId = null;
      }
    },
    async deletePromotion(item) {
      if (this.isHqLocked(item)) return;
      const { isConfirmed } = await confirm({
        props: this,
        text: "want_to_delete",
      });
      if (!isConfirmed) return;

      this.overlay = true;
      try {
        const { data } = await this.$http.delete(`promotions/${item.id}`);
        if (data?.status) {
          await success(this, data.message || "delete_promotion_success");
          const nextPage =
            this.promotions.length <= 1 && this.page > 1
              ? this.page - 1
              : this.page;
          await this.fetchPromotions(nextPage);
        }
      } finally {
        this.overlay = false;
      }
    },
  },
};
</script>

<style scoped>
.promotions-page {
  max-width: 1400px;
  margin: 0 auto;
}

.ps-page-title {
  font-size: 20px;
  font-weight: 800;
  color: #141021;
  letter-spacing: -0.01em;
}

.ps-page-subtitle {
  font-size: 12.5px;
  color: #64748b;
  margin-top: 2px;
}

/* Card Shell */
.ap-card {
  border-radius: 16px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  overflow: hidden;
  background-color: #ffffff !important;
}

.ap-card-accent {
  width: 3px;
  height: 18px;
  border-radius: 2px;
  background: #5b39c9;
  display: inline-block;
  flex-shrink: 0;
}

.border-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}

.border-top {
  border-top: 1px solid #e2e8f0 !important;
}

.bg-slate-50 {
  background-color: #f8fafc !important;
}

/* PosSmart Summary Metrics Cards */
.ps-summary-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  transition: all 0.15s ease;
}

.ps-summary-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.ps-summary-icon {
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
}

.ps-summary-icon--primary {
  background: rgba(91, 57, 201, 0.1);
  color: #5b39c9;
  border-color: rgba(91, 57, 201, 0.2);
}

.ps-summary-icon--info {
  background: #f0fdf4;
  color: #10b981;
  border-color: #a7f3d0;
}

.ps-summary-icon--cyan {
  background: #f0f9ff;
  color: #0284c7;
  border-color: #bae6fd;
}

.ps-summary-icon--amber {
  background: #fffbeb;
  color: #d97706;
  border-color: #fde68a;
}

.ps-summary-meta {
  min-width: 0;
  flex: 1 1 auto;
}

.ps-summary-label {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
}

.ps-summary-value {
  font-size: 18px;
  font-weight: 800;
  color: #1e293b;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

.ps-summary-value--info {
  color: #10b981;
}

.ps-summary-value--cyan {
  color: #0284c7;
}

.ps-summary-value--amber {
  color: #d97706;
}

/* PosSmart Segmented Tabs */
.br-tabs {
  display: inline-flex;
  height: 38px;
  box-sizing: border-box;
  align-items: center;
  gap: 4px;
  padding: 3px;
  border-radius: 10px;
  background: #f1f2f7;
  border: 1px solid #e2e8f0;
}

.br-tab {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 4px 12px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  color: #64748b;
  transition: all 0.15s ease;
}

.br-tab:hover {
  color: #141021;
}

.br-tab--active {
  color: #5b39c9 !important;
  background: #ffffff !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  font-weight: 700;
}

.br-tab-badge {
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 999px;
  background: #ede9fe;
  color: #5b39c9;
  font-weight: 700;
}

/* Filter Pills */
.ps-filter-pill {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.ps-filter-pill:hover {
  border-color: #5b39c9;
  color: #5b39c9;
}

.ps-filter-pill--active {
  background: #5b39c9;
  border-color: #5b39c9;
  color: #ffffff !important;
}

.ps-filter-pill--active .ps-filter-count {
  background: rgba(255, 255, 255, 0.25);
  color: #ffffff;
}

.ps-filter-count {
  font-size: 10px;
  background: #e2e8f0;
  color: #64748b;
  padding: 1px 5px;
  border-radius: 999px;
  margin-left: 4px;
  font-weight: 700;
}

/* Promo Card Styling */
.ps-promo-card {
  transition: transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
  height: 100%;
}

.ps-promo-card:hover {
  transform: translateY(-2px);
  border-color: #5b39c9 !important;
  box-shadow: 0 8px 24px rgba(91, 57, 201, 0.08) !important;
}

.ps-promo-card--inactive {
  opacity: 0.72;
}

.ps-promo-type-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  background: #ede9fe;
  color: #5b39c9;
}

.ps-promo-type--buyx_gety {
  background: #fef3c7;
  color: #d97706;
}

.ps-promo-type--discount_amount {
  background: #e0f2fe;
  color: #0284c7;
}

.ps-promo-type--bundle {
  background: #ccfbf1;
  color: #0d9488;
}

.ps-target-badge {
  font-size: 10.5px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #64748b;
}

.ps-promo-thumb-wrap {
  width: 74px;
  height: 74px;
}

.ps-promo-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border: 1px solid #e2e8f0;
}

.ps-promo-photo-placeholder {
  width: 100%;
  height: 100%;
  background: #f3f0fc;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ps-promo-name {
  font-size: 15px;
  font-weight: 700;
  color: #141021;
}

.ps-code-ticket {
  font-family: monospace;
  font-weight: 700;
  font-size: 11.5px;
  padding: 1px 7px;
  border-radius: 6px;
  background: #f3f0fc;
  color: #5b39c9;
  border: 1px dashed #cbd5e1;
  display: inline-flex;
  align-items: center;
}

.ps-promo-desc {
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.4;
}

.ps-usage-tracker {
  background: #f8fafc;
  border: 1px solid #f1f5f9;
}

/* Stock / Status Pill */
.stock-pill {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 600;
  white-space: nowrap;
}

.stock-pill-normal {
  background: #f0fdf4;
  color: #16a34a;
}

.stock-pill-slate {
  background: #f1f5f9;
  color: #64748b;
}

/* Action button */
.ps-act {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #64748b;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.ps-act:hover {
  color: #5b39c9;
  border-color: #5b39c9;
  background: rgba(91, 57, 201, 0.06);
}

.ps-act--danger:hover {
  color: #ef4444;
  border-color: #ef4444;
  background: #fef2f2;
}

.ps-per-page-select {
  width: 80px;
}

.ps-per-page-select >>> .v-input__slot {
  min-height: 30px !important;
  border-radius: 6px !important;
}

.ps-per-page-select >>> .v-select__selection {
  font-size: 12px;
}

.ps-empty-icon {
  color: #cbd5e1 !important;
}

/* Dark Theme Overrides */
.theme--dark .ps-page-title {
  color: #ffffff;
}

.theme--dark .ps-page-subtitle {
  color: #94a3b8;
}

.theme--dark .ap-card {
  border-color: rgba(255, 255, 255, 0.08) !important;
  background-color: #1e1e2d !important;
}

.theme--dark .border-bottom {
  border-bottom-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .border-top {
  border-top-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .bg-slate-50 {
  background-color: #262638 !important;
}

.theme--dark .br-tabs {
  background: #1b1d30;
  border-color: rgba(154, 140, 240, 0.16);
}

.theme--dark .br-tab {
  color: #7c82a0;
}

.theme--dark .br-tab--active {
  color: #9a8cf0 !important;
  background: rgba(154, 140, 240, 0.18) !important;
}

.theme--dark .ps-filter-pill {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.08);
  color: #94a3b8;
}

.theme--dark .ps-filter-pill:hover {
  border-color: #818cf8;
  color: #c7d2fe;
}

.theme--dark .ps-filter-pill--active {
  background: #5b39c9;
  color: #ffffff !important;
}

.theme--dark .ps-summary-card {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .ps-summary-card:hover {
  border-color: rgba(255, 255, 255, 0.16);
}

.theme--dark .ps-summary-icon--primary {
  background: rgba(129, 140, 248, 0.15);
  color: #a5b4fc;
  border-color: rgba(129, 140, 248, 0.25);
}

.theme--dark .ps-summary-icon--info {
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
  border-color: rgba(16, 185, 129, 0.25);
}

.theme--dark .ps-summary-icon--cyan {
  background: rgba(56, 189, 248, 0.15);
  color: #38bdf8;
  border-color: rgba(56, 189, 248, 0.25);
}

.theme--dark .ps-summary-icon--amber {
  background: rgba(245, 158, 11, 0.15);
  color: #fbbf24;
  border-color: rgba(245, 158, 11, 0.25);
}

.theme--dark .ps-summary-value {
  color: #ffffff;
}

.theme--dark .ps-promo-card {
  border-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .ps-promo-name {
  color: #ffffff;
}

.theme--dark .ps-promo-desc {
  color: #94a3b8;
}

.theme--dark .ps-promo-photo-placeholder {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .ps-code-ticket {
  background: #262638;
  color: #c7d2fe;
  border-color: rgba(255, 255, 255, 0.15);
}

.theme--dark .ps-usage-tracker {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.06);
}

.theme--dark .ps-act {
  background: #1e1e2d;
  border-color: rgba(255, 255, 255, 0.1);
  color: #94a3b8;
}

.theme--dark .ps-act:hover {
  color: #a5b4fc;
  border-color: #818cf8;
  background: rgba(129, 140, 248, 0.15);
}

.theme--dark .ps-act--danger:hover {
  color: #f87171;
  border-color: #ef4444;
  background: rgba(239, 68, 68, 0.15);
}

.theme--dark .ps-empty-icon {
  color: #475569 !important;
}
</style>
